import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * AccountActionBanner — sticky red strip shown when the backend has tripped
 * the impossible-travel auto-action (`account_action` on /auth/me).
 *
 * Two CTAs:
 *   • "Yes, that was me" → POST /me/account/acknowledge → clears the flag.
 *   • "About the Family Plan" → /pricing (cross-link to upsell).
 *
 * Stays mounted at the top of AppLayout until acknowledged. Designed to be
 * polite, not punitive — the goal is to convert a sharer into either a
 * legitimate one-person user or a future Family Plan buyer.
 */
export default function AccountActionBanner() {
  const { user, refreshUser } = useAuth();
  const [busy, setBusy] = useState(false);

  const action = user?.account_action;
  if (!action) return null;

  const fromLabel = action?.last_anomaly?.from_geo?.label || "one location";
  const toLabel = action?.last_anomaly?.to_geo?.label || "another location";

  const acknowledge = async () => {
    setBusy(true);
    try {
      await api.post("/me/account/acknowledge");
      toast.success("Thanks — we've cleared the alert.");
      if (refreshUser) await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clear alert");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-2xl bg-[#FCF0EC] border border-[#E8C4B8] p-4 sm:p-5 mb-5"
      data-testid="account-action-banner"
    >
      <div className="flex items-start gap-3">
        <span className="text-xl flex-none mt-0.5">🚩</span>
        <div className="flex-1 min-w-0">
          <div className="text-[#A33D26] font-semibold text-sm sm:text-base">
            We noticed unusual sign-in activity on your account
          </div>
          <p className="text-[#5C6B61] text-xs sm:text-sm mt-1 leading-relaxed">
            Recent sign-ins came from <strong>{fromLabel}</strong> and <strong>{toLabel}</strong> — closer together than any plane could travel. If that was you (e.g. travelling, VPN), tap below to dismiss. If your account is being shared, a Family Plan will land soon — until then, please ask others to start their own free account.
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            <button
              onClick={acknowledge}
              disabled={busy}
              data-testid="account-action-acknowledge"
              className="ff-btn-primary text-xs sm:text-sm disabled:opacity-60"
            >
              {busy ? "Clearing…" : "Yes, that was me"}
            </button>
            <Link
              to="/app/profile"
              data-testid="account-action-change-password"
              className="ff-btn-ghost text-xs sm:text-sm border border-[#E0D5C2]"
            >
              Change my password
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
